import { useState } from "react";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { useIntl, FormattedMessage } from "react-intl";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import moment from "moment";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useToasts } from "react-toast-notifications";

import { MemberStatus } from "../../../../../../settings/MemberStatus";
import {
  YYYY_MM_DD_FORMAT,
  DDDD_MMMM_YYYY_FORMAT,
} from "../../../../../../settings/constants";
import { DocumentImage } from "../../../../../../components/document_image/DocumentImage";
import { BranchManagerService } from "../../../../../../services/BranchManagerService";
import { Toaster } from "../../../../../../utility/toaster";
import {
  IdentificationTypeIdEnum,
  IdentificationTypeTranslations,
} from "../../../../../../attributes/user-attributes";

const useStyles = makeStyles((theme) => ({
  main: {
    width: "100%",
    minHeight: "77vh",
  },
  new: {
    fontSize: 12,
    color: theme.palette.primary.light,
    backgroundColor: "#D6E6FF",
    fontWeight: "bold",
  },
  pending: {
    fontSize: 12,
    color: "#F2994A",
    fontWeight: "bold",
  },
  approved: {
    fontSize: 12,
    color: "#219653",
    fontWeight: "bold",
  },
  avatar: {
    width: 55,
    height: 55,
    borderRadius: 40,
    marginRight: 10,
  },
  name: {
    fontSize: 16,
    fontWeight: "bold",
  },
  grey14: {
    fontSize: 14,
    color: theme.palette.grey[500],
  },
  grey16: {
    fontSize: 16,
    color: theme.palette.grey[500],
  },
  darkBold14: {
    fontSize: 14,
    fontWeight: "bold",
  },
  divider: {
    marginTop: 20,
    marginBottom: 20,
  },
  buttonRequestMoreInfo: {
    margin: 5,
    textTransform: "none",
    color: theme.palette.primary.dark,
  },
  buttonDecline: {
    color: theme.palette.common.white,
    backgroundColor: "#FF2929",
    margin: 5,
    textTransform: "none",
    "&:hover": {
      color: "#FF2929",
    },
  },
  buttonApprove: {
    color: theme.palette.common.white,
    backgroundColor: "#219653",
    margin: 5,
    textTransform: "none",
    "&:hover": {
      color: "#219653",
    },
  },
}));

interface EmployeeRecordProps {
  id: number;
  profilePicture: string;
  name: string;
  country: string;
  city: string;
  status: MemberStatus;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  identificationType: IdentificationTypeIdEnum;
  identificationNumber: string;
  identificationExpirationDate: string;
  paths: any[];
  loadEmployees: () => void;
}

export const EmployeeRecord = (props: EmployeeRecordProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { addToast } = useToasts();
  const {
    id,
    profilePicture,
    name,
    country,
    city,
    status,
    email,
    phone,
    dateOfBirth,
    address,
    identificationType,
    identificationNumber,
    identificationExpirationDate,
    paths,
    loadEmployees,
  } = props;
  const [approving, setApproving] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [requestingMore, setRequestingMore] = useState(false);

  const approve = async () => {
    setApproving(true);
    const approveResult = await BranchManagerService.approveEmployee({
      employeeId: id,
    });
    if (approveResult.isSuccess && approveResult.data.code === 100) {
      loadEmployees();
    } else {
      Toaster.error(
        addToast,
        formatMessage({ id: "sharedScreen.AnErrorOccurredWhileApproving" })
      );
    }
    setApproving(false);
  };

  const decline = async () => {
    setDeclining(true);
    const declineResult = await BranchManagerService.declineEmployee({
      employeeId: id,
    });
    if (declineResult.isSuccess && declineResult.data.code === 100) {
      loadEmployees();
    } else {
      Toaster.error(
        addToast,
        formatMessage({ id: "sharedScreen.AnErrorOccurredWhileDeclining" })
      );
    }
    setDeclining(false);
  };

  const requestMoreInfo = async () => {
    setRequestingMore(true);
    const requestMoreResult = await BranchManagerService.requestMoreInfoFromEmployee(
      { employeeId: id }
    );
    if (requestMoreResult.isSuccess && requestMoreResult.data.code === 100) {
      loadEmployees();
    } else {
      Toaster.error(
        addToast,
        formatMessage({
          id: "sharedScreen.AnErrorOccurredWhileRequestingMoreInfo",
        })
      );
    }
    setRequestingMore(false);
  };

  return (
    <Grid className={classes.main} item xs={12} lg={12}>
      <Grid
        container
        item
        xs={12}
        lg={12}
        justify="space-between"
        alignItems="center"
      >
        <Grid container direction="row" alignItems="center" item xs={8} lg={8}>
          <img
            className={classes.avatar}
            alt="user-avatar"
            src={profilePicture ?? process.env.PUBLIC_URL + "/user_avatar.png"}
          />
          <Grid direction="column">
            <Typography className={classes.name} variant="h4" component="h4">
              {name}
            </Typography>
            <Typography className={classes.grey14} variant="h5" component="h5">
              {city},{country}
            </Typography>
          </Grid>
        </Grid>
        <Button
          className={
            status === MemberStatus.new
              ? classes.new
              : status === MemberStatus.pending
              ? classes.pending
              : classes.approved
          }
        >
          {status === MemberStatus.new ? (
            <FormattedMessage id="sharedScreen.newAccount" />
          ) : status === MemberStatus.pending ? (
            <FormattedMessage id="sharedScreen.pendingAccount" />
          ) : (
            <FormattedMessage id="sharedScreen.approvedAccount" />
          )}
        </Button>
      </Grid>
      <Divider className={classes.divider} />
      <Grid className={classes.divider} container item xs={12} lg={12}>
        <Grid item xs={6} lg={6} direction="column">
          <Typography className={classes.grey14} variant="h5" component="h5">
            <FormattedMessage id="sharedScreen.email" />
          </Typography>
          <Typography className={classes.grey16} variant="h4" component="h4">
            {email}
          </Typography>
        </Grid>
        <Grid item xs={6} lg={6}>
          <Typography className={classes.grey14} variant="h5" component="h5">
            <FormattedMessage id="sharedScreen.phone" />
          </Typography>
          <Typography className={classes.grey16} variant="h4" component="h4">
            {phone}
          </Typography>
        </Grid>
      </Grid>
      <Grid className={classes.divider} container item xs={12} lg={12}>
        <Grid item xs={6} lg={6} direction="column">
          <Typography className={classes.grey14} variant="h5" component="h5">
            <FormattedMessage id="sharedScreen.dateOfBirth" />
          </Typography>
          <Typography className={classes.grey16} variant="h4" component="h4">
            {moment(dateOfBirth, YYYY_MM_DD_FORMAT).format(
              DDDD_MMMM_YYYY_FORMAT
            )}
          </Typography>
        </Grid>
      </Grid>
      <Grid className={classes.divider} container item xs={12} lg={12}>
        <Grid item xs={6} lg={6} direction="column">
          <Typography className={classes.grey14} variant="h5" component="h5">
            <FormattedMessage id="sharedScreen.address" />
          </Typography>
          <Typography className={classes.grey16} variant="h4" component="h4">
            {address}
          </Typography>
        </Grid>
      </Grid>
      <Grid className={classes.divider} item xs={12} lg={12}>
        <Typography className={classes.darkBold14} variant="h5" component="h5">
          <FormattedMessage id="sharedScreen.documents" />
        </Typography>
      </Grid>
      <Grid className={classes.divider} container item xs={12} lg={12}>
        <Grid item xs={6} lg={6} direction="column">
          <Typography className={classes.grey14} variant="h5" component="h5">
            <FormattedMessage
              id={IdentificationTypeTranslations[identificationType]}
            />
          </Typography>
          <Typography className={classes.grey16} variant="h4" component="h4">
            {identificationNumber}
          </Typography>
        </Grid>
        <Grid item xs={6} lg={6}>
          <Typography className={classes.grey14} variant="h5" component="h5">
            <FormattedMessage id="sharedScreen.expirationDate" />
          </Typography>
          <Typography className={classes.grey16} variant="h4" component="h4">
            {moment(identificationExpirationDate, YYYY_MM_DD_FORMAT).format(
              DDDD_MMMM_YYYY_FORMAT
            )}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        className={classes.divider}
        item
        xs={12}
        lg={12}
        spacing={2}
      >
        {paths.length && (
          <Grid item xs={6} lg={6}>
            <DocumentImage image={paths[0]} />
          </Grid>
        )}
        {paths.length > 1 && (
          <Grid item xs={6} lg={6}>
            <DocumentImage image={paths[1]} />
          </Grid>
        )}
      </Grid>
      {status === MemberStatus.new && (
        <Grid
          container
          className={classes.divider}
          item
          xs={12}
          lg={12}
          spacing={2}
        >
          <Grid item xs={6} lg={6}>
            <Button
              disabled={requestingMore}
              onClick={requestMoreInfo}
              className={classes.buttonRequestMoreInfo}
              variant="outlined"
            >
              <FormattedMessage id="sharedScreen.requestMoreInfo" />
              {requestingMore && <CircularProgress size={30} />}
            </Button>
          </Grid>
          <Grid
            item
            xs={6}
            lg={6}
            container
            direction="row"
            justify="flex-end"
            alignItems="center"
          >
            <Button
              disabled={declining}
              onClick={decline}
              className={classes.buttonDecline}
            >
              <FormattedMessage id="sharedScreen.decline" />
              {declining && <CircularProgress size={30} />}
            </Button>
            <Button
              disabled={approving}
              onClick={approve}
              className={classes.buttonApprove}
            >
              <FormattedMessage id="sharedScreen.approve" />
              {approving && <CircularProgress size={30} />}
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
